
import { supabase } from "@/lib/supabase";
import { Conversation } from "@/types/conversation";

export async function fetchRefundRequest(id: string): Promise<Conversation> {
  console.log("🔍 fetchRefundRequest: Fetching request with ID:", id);
  
  // First try to fetch from refund_requests table
  const { data: refundData, error: refundError } = await supabase
    .from("refund_requests")
    .select("*")
    .eq("id", id)
    .single();
  
  if (refundData && !refundError) {
    console.log("✅ fetchRefundRequest: Found regular refund request:", {
      id: refundData.id,
      message_format: refundData.message_format,
      type: "refund_request"
    });
    
    return {
      ...refundData,
      message_format: (refundData.message_format as "email" | "chat") || "email",
      request_type: "refund_request" // Add type identifier
    };
  }
  
  // If not found in refund_requests, try flight_compensation_claims
  const { data: flightData, error: flightError } = await supabase
    .from("flight_compensation_claims")
    .select("*")
    .eq("id", id)
    .single();
  
  if (flightData && !flightError) {
    console.log("✅ fetchRefundRequest: Found flight compensation claim:", {
      id: flightData.id,
      airline: flightData.airline,
      type: "flight_compensation"
    });
    
    // Map flight compensation claim to conversation format
    return {
      id: flightData.id,
      merchant: flightData.airline, // Use airline as merchant
      product: `Flight ${flightData.flight_number}`, // Flight info as product
      amount: flightData.compensation_amount || 0,
      currency: "EUR", // Default for flight compensation
      issue: `${flightData.flight_status} - ${flightData.departure_airport} to ${flightData.arrival_airport}`,
      status: flightData.claim_status,
      order_number: flightData.flight_number,
      purchase_date: flightData.departure_date,
      created_at: flightData.created_at,
      user_id: flightData.user_id,
      updated_at: flightData.updated_at,
      message_format: "email" as const,
      request_type: "flight_compensation", // Add type identifier
      generated_letter: flightData.generated_letter // Include the claim letter
    };
  }
  
  // If not found in either table, throw error
  console.error("❌ fetchRefundRequest: Request not found in either table");
  throw new Error("Request not found");
}

export async function fetchUserRefundRequests(): Promise<Conversation[]> {
  const { data: user } = await supabase.auth.getUser();
  
  if (!user.user) {
    throw new Error("You must be logged in to view refund requests");
  }
  
  // Fetch regular refund requests
  const { data: refundData, error: refundError } = await supabase
    .from("refund_requests")
    .select("*")
    .eq("user_id", user.user.id)
    .order("created_at", { ascending: false });
  
  if (refundError) throw refundError;
  
  // Fetch flight compensation claims
  console.log("🔍 Fetching flight compensation claims...");
  const { data: flightData, error: flightError } = await supabase
    .from("flight_compensation_claims")
    .select("*")
    .eq("user_id", user.user.id)
    .order("created_at", { ascending: false });
  
  console.log("Flight query result:", { flightData, flightError });
  
  if (flightError) {
    console.error("Flight compensation claims error:", flightError);
    throw flightError;
  }
  
  // Map refund requests to conversation format
  const refundConversations: Conversation[] = (refundData || []).map(item => ({
    ...item,
    message_format: (item.message_format as "email" | "chat") || "email",
    request_type: "refund_request" as const
  }));
  
  // Map flight compensation claims to conversation format
  const flightConversations: Conversation[] = (flightData || []).map(item => ({
    id: item.id,
    merchant: item.airline,
    product: `Flight ${item.flight_number}`,
    amount: item.compensation_amount || 0,
    currency: "EUR",
    issue: `${item.flight_status} - ${item.departure_airport} to ${item.arrival_airport}`,
    status: item.claim_status,
    order_number: item.flight_number,
    purchase_date: item.departure_date,
    created_at: item.created_at,
    user_id: item.user_id,
    updated_at: item.updated_at,
    message_format: "email" as const,
    request_type: "flight_compensation" as const,
    generated_letter: item.generated_letter // Include the claim letter
  }));
  
  // Combine and sort by created_at
  const allConversations = [...refundConversations, ...flightConversations];
  return allConversations.sort((a, b) => 
    new Date(b.created_at || '').getTime() - new Date(a.created_at || '').getTime()
  );
}
